/* eslint-disable prettier/prettier */
import { useCallback, useState } from 'react';
import {
	courseBulkEnrollmentService,
	BulkEnrollResponse,
	CoacheeListItem,
	GetCoacheesFilters,
} from '../services/CourseBulkEnrollment';

interface UseCourseBulkEnrollmentReturn {
	bulkEnroll: (
		courseId: string,
		userIds: string[]
	) => Promise<BulkEnrollResponse | null>;

	getCoacheesList: (
		filters: GetCoacheesFilters
	) => Promise<CoacheeListItem[]>;

	loading: boolean;
	bulkEnrollLoading: boolean;
	coacheesLoading: boolean;

	error: string | null;
	bulkEnrollError: string | null;
	coacheesError: string | null;

	clearError: () => void;
}

const getErrorMessage = (error: any): string => {
	if (error?.response?.data?.message) {
		return error.response.data.message;
	}

	if (error?.message) {
		return error.message;
	}

	return 'Something went wrong. Please try again.';
};

export const useCourseBulkEnrollment = (): UseCourseBulkEnrollmentReturn => {
	const [bulkEnrollLoading, setBulkEnrollLoading] = useState(false);
	const [coacheesLoading, setCoacheesLoading] = useState(false);

	const [bulkEnrollError, setBulkEnrollError] = useState<string | null>(
		null
	);
	const [coacheesError, setCoacheesError] = useState<string | null>(null);

	/**
	 * Bulk enroll learners into a course.
	 */
	const bulkEnroll = useCallback(
		async (
			courseId: string,
			userIds: string[]
		): Promise<BulkEnrollResponse | null> => {
			try {
				setBulkEnrollLoading(true);
				setBulkEnrollError(null);

				if (!courseId) {
					throw new Error('Course ID is required.');
				}

				if (!userIds || userIds.length === 0) {
					throw new Error('Please select at least one learner.');
				}

				const response = await courseBulkEnrollmentService.bulkEnroll(
					courseId,
					userIds
				);

				if (!response?.success) {
					const message =
						response?.message ||
						'Bulk enrollment failed. Please try again.';

					setBulkEnrollError(message);
					return null;
				}

				return response.data ?? null;
			} catch (error: any) {
				console.error('Error in bulkEnroll hook:', error);

				const message = getErrorMessage(error);

				setBulkEnrollError(message);

				return null;
			} finally {
				setBulkEnrollLoading(false);
			}
		},
		[]
	);

	/**
	 * Fetch coachees/mentees.
	 */
	const getCoacheesList = useCallback(
		async (filters: GetCoacheesFilters): Promise<CoacheeListItem[]> => {
			try {
				setCoacheesLoading(true);
				setCoacheesError(null);

				if (!filters?.type) {
					throw new Error('Learner type is required.');
				}

				const response =
					await courseBulkEnrollmentService.getCoacheesList(filters);

				if (!response?.success) {
					const message =
						response?.message ||
						'Failed to fetch learners. Please try again.';

					setCoacheesError(message);
					return [];
				}

				return response.data ?? [];
			} catch (error: any) {
				console.error('Error in getCoacheesList hook:', error);

				const message = getErrorMessage(error);

				setCoacheesError(message);

				return [];
			} finally {
				setCoacheesLoading(false);
			}
		},
		[]
	);

	/**
	 * Clear all errors.
	 */
	const clearError = useCallback(() => {
		try {
			setBulkEnrollError(null);
			setCoacheesError(null);
		} catch (error) {
			console.error('Error clearing enrollment errors:', error);
		}
	}, []);

	return {
		bulkEnroll,
		getCoacheesList,

		loading: bulkEnrollLoading || coacheesLoading,

		bulkEnrollLoading,
		coacheesLoading,

		error: bulkEnrollError || coacheesError,
		bulkEnrollError,
		coacheesError,

		clearError,
	};
};